import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetHeader,
  BottomSheetTitle,
  ChipKind,
  LegacySelectChip,
} from "@stubhub/broadway-web";
import { Picker } from "@viagogo/core/ui";
import type { PickerOption } from "@viagogo/core/ui";
import type { EventType } from "src/http/types";
import { Content, useContent } from "src/shared/components/content";
import {
  chipSubtleClasses,
  sectionContainer,
} from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/mobile-calendar-filter-row/styles";
import {
  FilterTypeChipContent,
  GRID_FILTER_TYPES,
} from "src/website-apps/shared/components/immersive-performer/eventlist-filters/constants";

interface Props {
  selectedEventType?: EventType | undefined;
  onEventTypeChange: (eventType: EventType) => void;
  otherDrawerOpen?: boolean;
}

export const MobileCalendarEventTypeSelector: FC<Props> = memo(
  ({ selectedEventType, onEventTypeChange, otherDrawerOpen }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const contentId =
      selectedEventType && FilterTypeChipContent[selectedEventType]
        ? FilterTypeChipContent[selectedEventType]
        : undefined;
    const labelContent = useContent(contentId ?? "cid$#3869221#"); // Events

    const eventsContent = useContent("cid$#3869221#"); // Events
    const parkingContent = useContent("cid$#1810823#"); // Parking

    const eventTypePickerOptions: PickerOption[] = useMemo(
      () => [
        { title: eventsContent, value: GRID_FILTER_TYPES.EVENTS },
        { title: parkingContent, value: GRID_FILTER_TYPES.PARKING },
      ],
      [eventsContent, parkingContent],
    );

    const handleChipClick = useCallback(() => {
      setIsDrawerOpen(true);
    }, []);

    const handleDrawerOpenChange = useCallback((isOpen: boolean) => {
      setIsDrawerOpen(isOpen);
    }, []);

    const handleEventTypeChange = useCallback(
      (value: PickerOption["value"]) => {
        onEventTypeChange(value as EventType);
        setIsDrawerOpen(false);
      },
      [onEventTypeChange],
    );

    useEffect(() => {
      if (otherDrawerOpen) {
        setIsDrawerOpen(false);
      }
    }, [otherDrawerOpen]);

    return (
      <>
        <LegacySelectChip
          label={labelContent}
          kind={ChipKind.Neutral}
          isOpen={isDrawerOpen}
          onClick={handleChipClick}
          classes={chipSubtleClasses}
        />
        <BottomSheet isOpen={isDrawerOpen} onOpenChange={handleDrawerOpenChange}>
          <BottomSheetHeader>
            <BottomSheetTitle>
              <Content id="cid$#3994924#" /> {/* Ticket Type */}
            </BottomSheetTitle>
          </BottomSheetHeader>
          <BottomSheetBody>
            <div className={sectionContainer}>
              <Picker
                options={eventTypePickerOptions}
                onChange={handleEventTypeChange}
                value={selectedEventType ?? GRID_FILTER_TYPES.EVENTS}
                isMultiSelect={false}
              />
            </div>
          </BottomSheetBody>
        </BottomSheet>
      </>
    );
  },
);
MobileCalendarEventTypeSelector.displayName = "MobileCalendarEventTypeSelector";
